import { alpha } from "@mui/material/styles";

// Draw the tree as branches: a trunk running down the left edge of every
// nested group, with a short stub reaching out to each item's icon —
// colour-coded per branch so Plant → WBS → category → item reads as a drawn,
// labeled tree instead of plain indentation. Rows are rounded pills that tint
// on hover and fill with the primary accent while selected.
export const treeViewSx = (theme) => {
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  const structuralLine = alpha(primary, isDark ? 0.35 : 0.25);
  const inertLine = alpha(theme.palette.text.primary, 0.14);
  const accentLine = alpha(theme.palette.warning.main, 0.6);

  const trunk = (color, width = 1.5) => ({
    borderLeft: `${width}px solid ${color}`,
  });
  const stub = (color, width = 1.5) => ({
    content: '""',
    position: "absolute",
    top: "50%",
    // Spans the group's 12px left padding, trunk to row edge.
    left: "-12px",
    width: "12px",
    height: 0,
    borderTop: `${width}px solid ${color}`,
  });

  return {
    flexGrow: 1,
    overflowY: "auto",
    px: 1,
    py: 1,
    "& .MuiTreeItem-content": {
      py: 0.5,
      px: 0.75,
      my: "1px",
      position: "relative",
      borderRadius: "8px",
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: alpha(primary, isDark ? 0.1 : 0.06),
      },
      // Room for the module badges - the default slot is a 15px square.
      "& .MuiTreeItem-iconContainer": {
        width: "auto",
        minWidth: "15px",
        marginRight: "8px",
      },
    },
    "& .MuiTreeItem-content.Mui-focused": {
      backgroundColor: alpha(primary, isDark ? 0.08 : 0.05),
    },
    "& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected.Mui-focused":
      {
        backgroundColor: alpha(primary, isDark ? 0.2 : 0.1),
        boxShadow: `inset 3px 0 0 ${primary}`,
        "& .MuiTreeItem-label": { fontWeight: 600 },
        "&:hover": {
          backgroundColor: alpha(primary, isDark ? 0.26 : 0.14),
        },
      },
    // Section headings (Estimation, Procurement, Project Control, …) - a
    // caption row with the section's badge rather than a named item, but the
    // rows they head are their children, so they open and close like any
    // other folder: they keep the arrow and answer to a click.
    "& .tree-section-label > .MuiTreeItem-content": {
      mt: 1,
      mb: 0.25,
      py: "3px !important",
      cursor: "pointer",
      // A heading only opens and closes - it never becomes the selection.
      "&.Mui-selected, &.Mui-selected.Mui-focused": {
        backgroundColor: "transparent",
        boxShadow: "none",
      },
      "& .MuiTreeItem-label": {
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "text.secondary",
        py: 0,
      },
      // Sized to the heading's own small type, so the arrow reads as part of
      // the caption rather than looming over it.
      "& .MuiTreeItem-iconContainer": {
        width: "14px",
        minWidth: "14px",
        marginRight: "4px",
        color: "text.disabled",
        "& svg": { fontSize: "14px" },
      },
    },
    "& .MuiTreeItem-group": {
      marginLeft: "17px",
      paddingLeft: "12px",
      ...trunk(structuralLine),
    },
    "& .MuiTreeItem-group > .MuiTreeItem-root > .MuiTreeItem-content::before":
      stub(structuralLine),
    "& .branch-inert > .MuiTreeItem-group": trunk(inertLine),
    "& .branch-inert > .MuiTreeItem-group > .MuiTreeItem-root > .MuiTreeItem-content::before":
      stub(inertLine),
    "& .branch-accent > .MuiTreeItem-group": trunk(accentLine),
    "& .branch-accent > .MuiTreeItem-group > .MuiTreeItem-root > .MuiTreeItem-content::before":
      stub(accentLine),
  };
};
